/**
 * ONE QUEUE FOR THE ART (2026-10-12).
 *
 * The valley used to ask for everything at once: the painting, every
 * child's eight painted frames and motion maps, and every animal's walk,
 * idle and turn sheets -- 130 to 170 files in the same instant on the wall,
 * with the painting itself waiting behind them. Now every piece of art that
 * code asks for goes through here: a few at a time, most important first,
 * each decoded before anyone is told it is ready, so nothing is drawn half
 * there.
 *
 *   0  the painting (it is an <img> and does not come through here, but
 *      everything else waits for it -- see LivingValley's `ready`)
 *   1  the children, and faces on the parent screens
 *   2  the animals, one species at a time
 *
 * A file is fetched and decoded once per page; asking again returns the
 * same promise.
 */

const PARALLEL = 4;

interface Job {
  url: string;
  priority: number;
  order: number;
  start: () => void;
}

const done = new Map<string, Promise<void>>();
const queue: Job[] = [];
let running = 0;
let order = 0;

function pump(): void {
  while (running < PARALLEL && queue.length) {
    queue.sort((a, b) => a.priority - b.priority || a.order - b.order);
    queue.shift()!.start();
  }
}

function decode(url: string): Promise<void> {
  if (typeof Image === 'undefined') return Promise.resolve();
  return new Promise<void>((resolve, reject) => {
    const image = new Image();
    image.decoding = 'async';
    image.onerror = () => reject(new Error(`Image unavailable: ${url}`));
    image.src = url;
    if (typeof image.decode === 'function') image.decode().then(resolve, reject);
    else image.onload = () => resolve();
  });
}

/** Fetch and decode `url`, in its turn. */
export function load(url: string, priority = 1): Promise<void> {
  const known = done.get(url);
  if (known) return known;
  const job = new Promise<void>((resolve, reject) => {
    queue.push({
      url,
      priority,
      order: order++,
      start: () => {
        running++;
        decode(url)
          .then(resolve, reject)
          .finally(() => {
            running--;
            pump();
          });
      },
    });
    pump();
  });
  // A failure is not remembered, so a later ask can try again.
  done.set(url, job);
  job.catch(() => done.delete(url));
  return job;
}

let avif: Promise<boolean> | null = null;
/** Whether this browser draws AVIF -- the format `image-set` will pick. */
export function takesAvif(): Promise<boolean> {
  if (typeof Image === 'undefined') return Promise.resolve(false);
  avif ??= new Promise((resolve) => {
    const probe = new Image();
    probe.onload = () => resolve(probe.width > 0);
    probe.onerror = () => resolve(false);
    probe.src =
      'data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=';
  });
  return avif;
}

/** A sprite sheet by its two files: the AVIF where it is drawn, else WebP. */
export async function loadSheet(avifUrl: string, webpUrl: string, priority = 2): Promise<void> {
  return load((await takesAvif()) ? avifUrl : webpUrl, priority);
}
